'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';

// ─── HANDED A LENS — ORGANIC EMERGENCE MECHANIC ──────────────────────────────

interface LensEmergenceState {
  hawasCount: number;
  fieldNotesCount: number;
  observationsCount: number;
  lensUnlocked: boolean;
  ripened: boolean;
}

export function HandedALensEmergence() {
  const [state, setState] = useState<LensEmergenceState>({
    hawasCount: 0,
    fieldNotesCount: 0,
    observationsCount: 0,
    lensUnlocked: false,
    ripened: false,
  });
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = localStorage.getItem('haal_lens_emergence');
    if (stored) {
      try { setState(JSON.parse(stored)); } catch {}
    }
  }, []);

  const totalActivity = state.hawasCount + state.fieldNotesCount + state.observationsCount;
  const ripenThreshold = 7;
  const progress = Math.min(totalActivity / ripenThreshold, 1);
  const isRipened = progress >= 1;

  const handleActivity = (type: 'hawas' | 'notes' | 'observations') => {
    setState(prev => {
      const next = {
        ...prev,
        hawasCount: type === 'hawas' ? prev.hawasCount + 1 : prev.hawasCount,
        fieldNotesCount: type === 'notes' ? prev.fieldNotesCount + 1 : prev.fieldNotesCount,
        observationsCount: type === 'observations' ? prev.observationsCount + 1 : prev.observationsCount,
        ripened: false,
        lensUnlocked: false,
      };
      const total = next.hawasCount + next.fieldNotesCount + next.observationsCount;
      next.ripened = total >= ripenThreshold;
      next.lensUnlocked = next.ripened;
      if (typeof window !== 'undefined') {
        localStorage.setItem('haal_lens_emergence', JSON.stringify(next));
      }
      return next;
    });
  };

  return (
    <div style={{
      background: 'rgba(6,13,18,0.8)',
      border: '1px solid rgba(45,138,135,0.25)',
      borderRadius: 10,
      overflow: 'hidden',
      margin: '24px 0',
    }}>
      {/* Header */}
      <div style={{
        padding: '18px 20px 14px',
        borderBottom: '1px solid rgba(45,138,135,0.1)',
        background: 'rgba(45,138,135,0.04)',
      }}>
        <div style={{ fontSize: 8, color: '#2d8a87', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 6 }}>
          ◎ PASSAGE 6 — "YOU'RE HANDED A LENS"
        </div>
        <div style={{ fontSize: 15, color: '#d4c5a9', marginBottom: 6 }}>
          The Lens Arrives When Looking Ripens
        </div>
        <p style={{ fontSize: 12, color: '#7a6f5d', lineHeight: 1.7, fontStyle: 'italic' }}>
          "After some time, you're handed a lens, and we go around again, looking — seeing, recalling — recognizing."
        </p>
      </div>

      {/* Passage annotation */}
      <div style={{ padding: '16px 20px' }}>
        <div style={{
          background: 'rgba(6,13,18,0.5)',
          border: '1px solid rgba(45,138,135,0.15)',
          borderLeft: '3px solid rgba(45,138,135,0.5)',
          borderRadius: '0 6px 6px 0',
          padding: '14px 16px',
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 8, color: '#2d8a87', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 6 }}>
            PLATFORM EXPRESSION — IRL LENS
          </div>
          <p style={{ fontSize: 12, color: '#9a8f7d', lineHeight: 1.7 }}>
            The IRL Lens is not the first module. It arrives when looking ripens. The caretaker begins by noticing, 
            documenting, threading thoughts into expressions — through the Hawas Dock (sensory observation) and 
            field notes. The lens appears when the garden observes that the caretaker has been looking consistently. 
            Not after completing X tasks, but after attention has ripened.
          </p>
        </div>

        {/* Ripening tracker */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: '#d4c5a9' }}>Attention Ripening</div>
            <div style={{ fontSize: 9, color: isRipened ? '#2d7a47' : '#2d8a87' }}>
              {isRipened ? '✓ The lens is ready to be handed' : `${totalActivity} / ${ripenThreshold} — still looking`}
            </div>
          </div>
          <div style={{
            height: 4,
            background: 'rgba(45,138,135,0.1)',
            borderRadius: 2,
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${progress * 100}%`,
              background: isRipened
                ? 'linear-gradient(90deg, #2d7a47, #5a8a5a)'
                : 'linear-gradient(90deg, #2d8a87, #4aada9)',
              borderRadius: 2,
              transition: 'width 0.4s ease',
            }} />
          </div>
        </div>

        {/* Activity types */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
          {[
            { type: 'hawas' as const, label: 'Hawas Dock', arabic: 'حَوَاس', count: state.hawasCount, color: '#2d8a87', desc: 'Sensory observations' },
            { type: 'notes' as const, label: 'Field Notes', arabic: 'مُلَاحَظَات', count: state.fieldNotesCount, color: '#c9a84c', desc: 'Written reflections' },
            { type: 'observations' as const, label: 'Observations', arabic: 'مُشَاهَدَات', count: state.observationsCount, color: '#2d7a47', desc: 'Submitted encounters' },
          ].map(item => (
            <button
              key={item.type}
              onClick={() => handleActivity(item.type)}
              style={{
                background: `${item.color}08`,
                border: `1px solid ${item.color}25`,
                borderRadius: 6,
                padding: '10px 8px',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: item.color, marginBottom: 2 }}>{item.arabic}</div>
              <div style={{ fontSize: 9, color: '#d4c5a9', marginBottom: 2 }}>{item.label}</div>
              <div style={{ fontSize: 9, color: '#7a6f5d', marginBottom: 6 }}>{item.desc}</div>
              <div style={{ fontSize: 16, color: item.color, fontWeight: 500 }}>{item.count}</div>
            </button>
          ))}
        </div>

        {/* Lens emergence */}
        {isRipened && (
          <div style={{
            background: 'rgba(45,122,71,0.08)',
            border: '1px solid rgba(45,122,71,0.3)',
            borderRadius: 8,
            padding: '16px',
            animation: 'lensEmerge 0.5s ease',
          }}>
            <style>{`@keyframes lensEmerge { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }`}</style>
            <div style={{ fontSize: 8, color: '#2d7a47', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>
              ◈ THE LENS IS HANDED
            </div>
            <p style={{ fontSize: 13, color: '#d4c5a9', lineHeight: 1.75, fontFamily: "'Georgia', serif", fontStyle: 'italic', marginBottom: 12 }}>
              "After some time, you're handed a lens, and we go around again, looking — seeing, recalling — recognizing."
            </p>
            <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.65, marginBottom: 12 }}>
              Your attention has ripened. The IRL Lens is now available — not as the first tool, but as the tool that arrives 
              when looking has already been cultivating. You've been walking. The lens deepens a journey already underway.
            </p>
            <Link
              href="/inner-reality-lens"
              style={{
                display: 'inline-block',
                fontSize: 11,
                color: '#2d7a47',
                textDecoration: 'none',
                border: '1px solid rgba(45,122,71,0.4)',
                borderRadius: 5,
                padding: '8px 16px',
                background: 'rgba(45,122,71,0.1)',
                letterSpacing: '.06em',
              }}
            >
              → Enter the IRL Lens — the lens is ready
            </Link>
          </div>
        )}

        {!isRipened && (
          <div style={{ fontSize: 10, color: '#4a4035', fontStyle: 'italic', textAlign: 'center' }}>
            The lens arrives when looking ripens. Keep observing, noting, submitting. The garden is watching.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── UBOOR THRESHOLD — GARDEN TRANSPARENCY AT HAQQ YAQEEN ───────────────────

export function UboorThreshold() {
  const [thresholdRevealed, setThresholdRevealed] = useState(false);
  const [stage, setStage] = useState<'ilm' | 'ayn' | 'haqq'>('ilm');

  const stages = [
    {
      id: 'ilm' as const,
      arabic: 'عِلْمُ الْيَقِين',
      label: 'Ilm Yaqeen',
      subtitle: 'Knowledge-Certainty',
      description: 'The caretaker has read about the Names, studied the Keys, browsed the fission sequences. They know the framework. This is the read-only stage — the first stage of the internet.',
      platformExpression: 'Levels 1 — the caretaker engages the platform as a learner. The garden is a library.',
      color: '#2d8a87',
      gardenState: 'The garden is vivid with new information. Every tile is fresh. The soil is being prepared.',
    },
    {
      id: 'ayn' as const,
      arabic: 'عَيْنُ الْيَقِين',
      label: 'Ayn Yaqeen',
      subtitle: 'Perception-Certainty',
      description: 'The caretaker has seen the Names at work. They\'ve done IRL Lens observations, extracted Names from encounters, watched the Sunnat Allah unfold in their ward. They\'ve seen evidence — through the platform, through screens, through the Mata of the garden.',
      platformExpression: 'Levels 2-5 — the caretaker engages the platform as a witness. The garden is a lens.',
      color: '#c9a84c',
      gardenState: 'The garden is active. Modules are cultivated. The garment has threads. The Mizan is visible.',
    },
    {
      id: 'haqq' as const,
      arabic: 'حَقُّ الْيَقِين',
      label: 'Haqq Yaqeen',
      subtitle: 'Reality-Certainty',
      description: 'The caretaker has experienced the Names directly. Not through the platform. Not through the lens. Through their own lived encounter with creation. They held the leaf. They stood in the rain. They served in the ward and felt Ar-Razzaq flowing through their hands. They closed the screen and opened the door.',
      platformExpression: 'Levels 6-7 — the platform cannot deliver this level. It can bring you to the threshold. Only creation itself can complete the crossing.',
      color: '#2d7a47',
      gardenState: 'The garden becomes transparent. The caretaker looks at their garden and sees through it to the creation it was always pointing toward.',
    },
  ];

  const activeStage = stages.find(s => s.id === stage)!;

  return (
    <div style={{
      background: 'rgba(6,13,18,0.85)',
      border: '1px solid rgba(45,122,71,0.2)',
      borderRadius: 10,
      overflow: 'hidden',
      margin: '24px 0',
    }}>
      {/* Header */}
      <div style={{
        padding: '18px 20px 14px',
        borderBottom: '1px solid rgba(45,122,71,0.1)',
        background: 'rgba(45,122,71,0.04)',
      }}>
        <div style={{ fontSize: 8, color: '#2d7a47', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 6 }}>
          ◈ UBOOR THRESHOLD — THE GARDEN'S HIGHEST ACHIEVEMENT
        </div>
        <div style={{ fontSize: 15, color: '#d4c5a9', marginBottom: 6 }}>
          The Garden's Highest Achievement Is Its Own Transparency
        </div>
        <p style={{ fontSize: 12, color: '#7a6f5d', lineHeight: 1.7, fontStyle: 'italic' }}>
          "The most mature caretaker is the one who doesn't need the garden anymore — who sees the Names without the lens, 
          who reads the Sunnat Allah without the timeline, who fulfills the Amanah without the quest system."
        </p>
      </div>

      {/* Stage selector */}
      <div style={{ padding: '16px 20px 0' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {stages.map(s => (
            <button
              key={s.id}
              onClick={() => setStage(s.id)}
              style={{
                flex: 1,
                background: stage === s.id ? `${s.color}15` : 'none',
                border: `1px solid ${stage === s.id ? s.color + '50' : s.color + '20'}`,
                color: stage === s.id ? s.color : '#7a6f5d',
                fontSize: 9,
                padding: '8px 4px',
                cursor: 'pointer',
                borderRadius: 4,
                letterSpacing: '.06em',
                textAlign: 'center',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontFamily: "'Amiri', serif", fontSize: 13, marginBottom: 2 }}>{s.arabic}</div>
              <div>{s.label}</div>
            </button>
          ))}
        </div>

        {/* Active stage detail */}
        <div style={{
          background: `${activeStage.color}06`,
          border: `1px solid ${activeStage.color}20`,
          borderRadius: 8,
          padding: '16px',
          marginBottom: 16,
          transition: 'all 0.2s',
        }}>
          <div style={{ fontSize: 9, color: activeStage.color, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 6 }}>
            {activeStage.subtitle}
          </div>
          <p style={{ fontSize: 12, color: '#d4c5a9', lineHeight: 1.75, marginBottom: 10 }}>
            {activeStage.description}
          </p>
          <div style={{
            background: 'rgba(6,13,18,0.4)',
            border: `1px solid ${activeStage.color}15`,
            borderRadius: 5,
            padding: '10px 12px',
            marginBottom: 10,
          }}>
            <div style={{ fontSize: 8, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>
              PLATFORM EXPRESSION
            </div>
            <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.6 }}>{activeStage.platformExpression}</p>
          </div>
          <div style={{
            background: `${activeStage.color}08`,
            border: `1px solid ${activeStage.color}15`,
            borderRadius: 5,
            padding: '10px 12px',
          }}>
            <div style={{ fontSize: 8, color: activeStage.color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>
              GARDEN STATE
            </div>
            <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.6, fontStyle: 'italic' }}>{activeStage.gardenState}</p>
          </div>
        </div>

        {/* Uboor moment */}
        {stage === 'haqq' && (
          <div style={{
            background: 'rgba(45,122,71,0.06)',
            border: '1px solid rgba(45,122,71,0.25)',
            borderRadius: 8,
            padding: '16px',
            marginBottom: 16,
          }}>
            <div style={{ fontSize: 8, color: '#2d7a47', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>
              ◈ THE UBOOR — THE CROSSING-OVER
            </div>
            <p style={{ fontSize: 12, color: '#d4c5a9', lineHeight: 1.75, fontFamily: "'Georgia', serif", fontStyle: 'italic', marginBottom: 10 }}>
              "The garden doesn't close. It becomes transparent. The caretaker looks at their garden and sees through it 
              to the creation it was always pointing toward."
            </p>
            <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.65, marginBottom: 12 }}>
              The cherry blossom passage makes this poignant: the founder writes to imprisoned caretakers — people who 
              cannot physically go to the waterfront. For them, Haqq Yaqeen is deferred. The platform exists partly to 
              serve those for whom direct encounter is restricted. The garden honors their reality while pointing toward 
              the day when they will stand among the blossoms themselves.
            </p>
            <div style={{
              background: 'rgba(6,13,18,0.5)',
              border: '1px solid rgba(45,122,71,0.15)',
              borderLeft: '3px solid rgba(45,122,71,0.5)',
              borderRadius: '0 5px 5px 0',
              padding: '10px 12px',
            }}>
              <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.65, fontStyle: 'italic' }}>
                The garden is Mata — a tool that served a utility, a purpose. But critically, that purpose wasn't 
                directly tied to the pleasure from the tool itself. Its purpose is fulfilled when the caretaker no 
                longer needs it — when they are among the blossoms, not reading about them.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Cherry blossom anchor */}
      <div style={{
        margin: '0 20px 20px',
        background: 'rgba(201,168,76,0.04)',
        border: '1px solid rgba(201,168,76,0.15)',
        borderRadius: 6,
        padding: '14px',
      }}>
        <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 8 }}>
          ◎ THE CHERRY BLOSSOM ANCHOR
        </div>
        <p style={{ fontSize: 12, color: '#9a8f7d', lineHeight: 1.7, fontStyle: 'italic' }}>
          "If you come here and experience them in person, could that be 'Haqq Yaqeen' — Reality of Certainty?"
        </p>
        <p style={{ fontSize: 11, color: '#7a6f5d', lineHeight: 1.6, marginTop: 6 }}>
          The platform can bring you to the threshold of Haqq Yaqeen. Only creation itself — only direct encounter 
          with the signs — can complete the crossing. The garden is the waterfront path. The blossoms are outside.
        </p>
      </div>
    </div>
  );
}

// ─── YAQEEN PROGRESSION ARCHITECTURE ─────────────────────────────────────────

export function YaqeenProgressionArchitecture() {
  const [activeLevel, setActiveLevel] = useState<'ilm' | 'ayn' | 'haqq'>('ilm');

  const levels = [
    {
      id: 'ilm' as const,
      arabic: 'عِلْمُ الْيَقِين',
      label: 'Ilm Yaqeen',
      number: '01',
      color: '#2d8a87',
      modules: ['Keys to Kulli Haal', 'SL Glossary', 'Open Letter', 'Names Reference'],
      description: 'You have read about the Names. You know the framework. The garden is a library.',
      cherryBlossom: 'Someone tells you cherry blossoms are blooming. You look it up — the tree is recorded as planted there. You know it. You are certain. But you have not seen it.',
      gardenState: 'Tiles are fresh. Soil is being prepared. The garment has no threads yet.',
      replaces: 'Depth Level 1-2',
    },
    {
      id: 'ayn' as const,
      arabic: 'عَيْنُ الْيَقِين',
      label: 'Ayn Yaqeen',
      number: '02',
      color: '#c9a84c',
      modules: ['IRL Lens', 'Observation Chain', 'Sequence Fission', 'Ayat in Creation'],
      description: 'You have seen the Names at work. You\'ve done IRL Lens observations, extracted Names from encounters. You\'ve seen evidence — through the platform.',
      cherryBlossom: 'You are shown photographs and video of the cherry blossoms in bloom. You have seen them. But you have not stood beneath them.',
      gardenState: 'Modules are cultivated. The garment has threads. The Mizan is visible. The garden is active.',
      replaces: 'Depth Levels 3-5',
    },
    {
      id: 'haqq' as const,
      arabic: 'حَقُّ الْيَقِين',
      label: 'Haqq Yaqeen',
      number: '03',
      color: '#2d7a47',
      modules: ['Ward Presence', 'Direct Service', 'MRG Fulfillment', 'Community Amanah'],
      description: 'You have experienced the Names directly. Not through the platform. Through your own lived encounter with creation. You held the leaf. You stood in the rain.',
      cherryBlossom: 'You walk to the waterfront. You stand beneath the cherry blossoms. The petals fall around you. The scent reaches you. The light through the branches is on your face. You are in it.',
      gardenState: 'The garden becomes transparent. You see through it to the creation it was always pointing toward. The Uboor Threshold.',
      replaces: 'Depth Levels 6-7',
    },
  ];

  const active = levels.find(l => l.id === activeLevel)!;

  return (
    <div style={{
      background: 'rgba(6,13,18,0.8)',
      border: '1px solid rgba(201,168,76,0.2)',
      borderRadius: 10,
      overflow: 'hidden',
      margin: '24px 0',
    }}>
      {/* Header */}
      <div style={{
        padding: '18px 20px 14px',
        borderBottom: '1px solid rgba(201,168,76,0.1)',
        background: 'rgba(201,168,76,0.03)',
      }}>
        <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 6 }}>
          ◈ PASSAGE 7 — CHERRY BLOSSOM / YAQEEN PROGRESSION
        </div>
        <div style={{ fontSize: 15, color: '#d4c5a9', marginBottom: 6 }}>
          The True Progression Metric
        </div>
        <p style={{ fontSize: 12, color: '#7a6f5d', lineHeight: 1.7 }}>
          Ilm → Ayn → Haqq Yaqeen replaces depth level numbers across all modules. 
          The progression is qualitative (closer to the blossoms), not quantitative (more observations, deeper levels).
        </p>
      </div>

      {/* Level selector */}
      <div style={{ padding: '16px 20px 0' }}>
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, border: '1px solid rgba(201,168,76,0.15)', borderRadius: 6, overflow: 'hidden' }}>
          {levels.map((l, i) => (
            <button
              key={l.id}
              onClick={() => setActiveLevel(l.id)}
              style={{
                flex: 1,
                background: activeLevel === l.id ? `${l.color}15` : 'none',
                border: 'none',
                borderRight: i < levels.length - 1 ? '1px solid rgba(201,168,76,0.1)' : 'none',
                color: activeLevel === l.id ? l.color : '#7a6f5d',
                fontSize: 9,
                padding: '10px 6px',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontFamily: "'Amiri', serif", fontSize: 14, marginBottom: 2 }}>{l.arabic}</div>
              <div style={{ letterSpacing: '.06em' }}>{l.label}</div>
              <div style={{ fontSize: 8, color: '#4a4035', marginTop: 2 }}>was: {l.replaces}</div>
            </button>
          ))}
        </div>

        {/* Active level detail */}
        <div style={{
          background: `${active.color}06`,
          border: `1px solid ${active.color}20`,
          borderRadius: 8,
          padding: '16px',
          marginBottom: 14,
          transition: 'all 0.2s',
        }}>
          <p style={{ fontSize: 12, color: '#d4c5a9', lineHeight: 1.75, marginBottom: 12 }}>
            {active.description}
          </p>

          {/* Cherry blossom example */}
          <div style={{
            background: 'rgba(6,13,18,0.5)',
            border: `1px solid ${active.color}15`,
            borderLeft: `3px solid ${active.color}50`,
            borderRadius: '0 5px 5px 0',
            padding: '10px 12px',
            marginBottom: 12,
          }}>
            <div style={{ fontSize: 8, color: active.color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>
              CHERRY BLOSSOM EXAMPLE
            </div>
            <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.65, fontStyle: 'italic' }}>
              {active.cherryBlossom}
            </p>
          </div>

          {/* Modules at this level */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 8, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>
              PLATFORM MODULES AT THIS LEVEL
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {active.modules.map(m => (
                <span key={m} style={{
                  fontSize: 9,
                  color: active.color,
                  background: `${active.color}10`,
                  border: `1px solid ${active.color}25`,
                  borderRadius: 3,
                  padding: '3px 7px',
                }}>
                  {m}
                </span>
              ))}
            </div>
          </div>

          {/* Garden state */}
          <div style={{ fontSize: 10, color: '#7a6f5d', fontStyle: 'italic' }}>
            Garden state: {active.gardenState}
          </div>
        </div>
      </div>

      {/* Architectural note */}
      <div style={{
        margin: '0 20px 20px',
        background: 'rgba(6,13,18,0.5)',
        border: '1px solid rgba(201,168,76,0.1)',
        borderRadius: 6,
        padding: '12px 14px',
      }}>
        <div style={{ fontSize: 8, color: '#7a6f5d', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 6 }}>
          ARCHITECTURAL IMPLICATION
        </div>
        <p style={{ fontSize: 11, color: '#7a6f5d', lineHeight: 1.65 }}>
          Every G-document uses depth levels, cultivation stages, and milestone counters. But the Yaqeen framework 
          is more precise and more honest: the progression is qualitative — closer to the blossoms — not quantitative. 
          One genuine value shift is worth more than a hundred module completions.
        </p>
      </div>
    </div>
  );
}

// ─── PASSAGE ANNOTATIONS — CONNECTING LETTER TO PLATFORM ─────────────────────

interface PassageAnnotation {
  passageId: string;
  passageTitle: string;
  passageText: string;
  platformConnections: {
    label: string;
    description: string;
    href: string;
    type: 'key' | 'lens' | 'mrg' | 'glossary' | 'garden' | 'bridge';
  }[];
  architecturalNote: string;
  color: string;
}

const PASSAGE_ANNOTATIONS: PassageAnnotation[] = [
  {
    passageId: 'i-wrote-that',
    passageTitle: '"I wrote that as an introduction"',
    passageText: 'I wrote that as an introduction to the Deen (Way of life) of Islam — submission, peace, connection.',
    platformConnections: [
      { label: 'Homepage Origin Statement', description: 'The garden was built by a person who walked this path. The homepage\'s origin statement carries this personal testimony.', href: '/homepage', type: 'garden' },
      { label: 'Open Letter — Founder\'s Thread', description: 'The founder\'s voice surfaces at key moments in the caretaker\'s journey, not as instruction but as companionship.', href: '/open-letter', type: 'key' },
    ],
    architecturalNote: 'The platform is a personal testimony made operational, not an anonymous system. The garden has an author.',
    color: '#c9a84c',
  },
  {
    passageId: 'walk-with-you',
    passageTitle: '"I\'d like to walk with you"',
    passageText: 'I\'d like to walk with you on a journey processing what I see, a bit of how I see it, and what I recognize on the way.',
    platformConnections: [
      { label: 'Guided Walkthrough', description: 'The walkthrough is structured as a walking companion experience — the founder\'s voice walking alongside the caretaker.', href: '/guided-walkthrough', type: 'bridge' },
      { label: 'Companion Voice Layer', description: 'The garden carries a companion voice that draws from the Open Letter\'s prose — not as quotes but as tonal presence.', href: '/user-dashboard', type: 'garden' },
    ],
    architecturalNote: 'The relationship precedes the framework. The walk precedes the method.',
    color: '#2d8a87',
  },
  {
    passageId: 'fitra-verse',
    passageTitle: 'Quran 30:30 — Fitra',
    passageText: 'So set your orientation to the way of life, upright, the original design of nature Allah made, which He has created mankind on it. No change in the creation Allah.',
    platformConnections: [
      { label: 'SL Glossary — Fitra Entry', description: 'Fitra is the foundational concept: before learning to see, recognize that you were designed to see.', href: '/sl-glossary', type: 'glossary' },
      { label: 'Keys — Key 01: Ayat', description: 'Key 01 re-activates the Fitra. The capacity for recognition is already in us before the platform teaches anything.', href: '/keys-to-kulli-haal', type: 'key' },
      { label: 'My Garden — Fitra Bedrock', description: 'The Fitra bedrock layer beneath the soil — indestructible, unchanging, the original ground.', href: '/user-dashboard', type: 'garden' },
    ],
    architecturalNote: 'Fitra is the garden\'s bedrock. The original design is what makes recognition possible; it cannot be erased, only obscured.',
    color: '#2d7a47',
  },
  {
    passageId: 'garments-seeds',
    passageTitle: '"These bodies are the garments we wear, the seeds we plant"',
    passageText: 'Life starts at the checkout, we still in the aisles. These bodies are the garments we wear, the seeds we plant.',
    platformConnections: [
      { label: 'My Garden — Reshatal Taqwah', description: 'The garment and the garden are two views of the same journey. What you cultivate is what you become.', href: '/user-dashboard', type: 'garden' },
      { label: 'Garment-Garden Feedback Loop', description: 'The garden\'s cultivation activity generates garment threads, and the garment\'s current state influences what the garden can grow.', href: '/user-dashboard', type: 'garden' },
    ],
    architecturalNote: 'Garment and Garden must be unified — two views of the same journey, not separate systems.',
    color: '#8a6a2a',
  },
  {
    passageId: 'the-mural',
    passageTitle: 'The Mural / Central Hub',
    passageText: 'The mural, a central hub with an overview of all regions. A path through the nearest emerges as a multi-layered forest watershed from highlands to valleys, canopy to roots.',
    platformConnections: [
      { label: 'Central Hub Diagram', description: 'The spiral mandala that serves as the garden\'s navigation architecture — beginning with the nearest.', href: '/user-dashboard', type: 'garden' },
      { label: 'Hikmah Station', description: 'Gathered information highlighting connections, processes, and diagrams unfolding how resources flow throughout habitats.', href: '/open-letter#section-the-mural', type: 'bridge' },
    ],
    architecturalNote: 'The spiral begins with "the nearest." The mural opens, it doesn\'t display. Begin with what\'s closest.',
    color: '#5a8a5a',
  },
  {
    passageId: 'handed-lens',
    passageTitle: '"You\'re handed a lens"',
    passageText: 'After some time, you\'re handed a lens, and we go around again, looking — seeing, recalling — recognizing.',
    platformConnections: [
      { label: 'IRL Lens — Organic Emergence', description: 'The IRL Lens should not be the first module. It arrives when attention ripens — after Hawas Dock and field notes activity.', href: '/inner-reality-lens', type: 'lens' },
      { label: 'Bridge 02', description: 'The bridge between observation and recognition — the lens deepens a journey already underway.', href: '/bridge-irl-application', type: 'bridge' },
    ],
    architecturalNote: 'The lens is handed, not chosen. It arrives when attention ripens; the IRL Lens should not be the first module.',
    color: '#2d8a87',
  },
  {
    passageId: 'cherry-blossom',
    passageTitle: 'Cherry Blossom / Yaqeen',
    passageText: 'I can tell you about something; for example, that cherry blossoms are blooming by the waterfront behind the museum down the street from here.',
    platformConnections: [
      { label: 'Batin Depth Unlock', description: 'The Yaqeen framework is the true progression metric — Ilm/Ayn/Haqq replacing depth level numbers.', href: '/user-dashboard', type: 'garden' },
      { label: 'Observation Submission Architecture', description: 'The observation system supports all three Yaqeen levels — from received knowledge to direct encounter.', href: '/inner-reality-lens', type: 'lens' },
    ],
    architecturalNote: 'The garden\'s highest achievement is its own transparency. Haqq Yaqeen is direct experience; the platform cannot deliver it, only point toward it.',
    color: '#c9a84c',
  },
];

export function PassageAnnotationsPanel() {
  const [activePassage, setActivePassage] = useState<string | null>(null);

  return (
    <div style={{
      background: 'rgba(6,13,18,0.8)',
      border: '1px solid rgba(201,168,76,0.15)',
      borderRadius: 10,
      overflow: 'hidden',
      margin: '24px 0',
    }}>
      {/* Header */}
      <div style={{
        padding: '18px 20px 14px',
        borderBottom: '1px solid rgba(201,168,76,0.1)',
        background: 'rgba(201,168,76,0.03)',
      }}>
        <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 6 }}>
          ◈ PASSAGE ANNOTATIONS — LETTER TO PLATFORM
        </div>
        <div style={{ fontSize: 15, color: '#d4c5a9', marginBottom: 6 }}>
          How Each Passage Manifests in the Platform
        </div>
        <p style={{ fontSize: 12, color: '#7a6f5d', lineHeight: 1.7 }}>
          Each passage from the Open Letter connects to a specific platform expression. 
          The letter is not a document about the platform — the platform is the letter, unfolded into habitable space.
        </p>
      </div>

      {/* Passage list */}
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {PASSAGE_ANNOTATIONS.map(p => (
            <div key={p.passageId} style={{
              background: activePassage === p.passageId ? `${p.color}08` : 'rgba(6,13,18,0.4)',
              border: `1px solid ${activePassage === p.passageId ? p.color + '35' : 'rgba(201,168,76,0.1)'}`,
              borderLeft: `3px solid ${p.color}${activePassage === p.passageId ? '' : '50'}`,
              borderRadius: '0 6px 6px 0',
              overflow: 'hidden',
              transition: 'all 0.2s',
            }}>
              <button
                onClick={() => setActivePassage(activePassage === p.passageId ? null : p.passageId)}
                style={{
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '12px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  textAlign: 'left',
                  gap: 10,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, color: '#d4c5a9', marginBottom: 3 }}>{p.passageTitle}</div>
                  <div style={{ fontSize: 10, color: '#7a6f5d', fontStyle: 'italic' }}>
                    "{p.passageText.substring(0, 80)}..."
                  </div>
                </div>
                <span style={{ color: p.color, fontSize: 14, flexShrink: 0 }}>
                  {activePassage === p.passageId ? '−' : '+'}
                </span>
              </button>

              {activePassage === p.passageId && (
                <div style={{ padding: '0 14px 14px', animation: 'passageExpand 0.2s ease' }}>
                  <style>{`@keyframes passageExpand { from { opacity: 0; } to { opacity: 1; } }`}</style>

                  {/* Platform connections */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 8, color: '#7a6f5d', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 8 }}>
                      PLATFORM CONNECTIONS
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {p.platformConnections.map((conn, i) => (
                        <Link
                          key={i}
                          href={conn.href}
                          style={{
                            display: 'block',
                            background: 'rgba(6,13,18,0.5)',
                            border: `1px solid ${p.color}20`,
                            borderRadius: 5,
                            padding: '10px 12px',
                            textDecoration: 'none',
                            transition: 'border-color 0.15s',
                          }}
                        >
                          <div style={{ fontSize: 10, color: p.color, marginBottom: 3 }}>→ {conn.label}</div>
                          <div style={{ fontSize: 10, color: '#7a6f5d', lineHeight: 1.5 }}>{conn.description}</div>
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Architectural note */}
                  <div style={{
                    background: `${p.color}06`,
                    border: `1px solid ${p.color}15`,
                    borderRadius: 5,
                    padding: '10px 12px',
                  }}>
                    <div style={{ fontSize: 8, color: p.color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                      ARCHITECTURAL NOTE
                    </div>
                    <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.6, fontStyle: 'italic' }}>
                      {p.architecturalNote}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── FIVE SECTION 2 DEMANDS ───────────────────────────────────────────────────

export function FiveSectionTwoDemands() {
  const [activeDemand, setActiveDemand] = useState<number | null>(null);
  const [fikrText, setFikrText] = useState('');
  const [fikrSaved, setFikrSaved] = useState(false);

  const demands = [
    {
      number: '01',
      title: 'The Personal Voice Must Be Present',
      arabic: 'الصَّوْت الشَّخْصِي',
      description: 'The Open Letter is not a whitepaper. It\'s a letter from a person. The site should carry the founder\'s companionship — not as a chatbot or avatar, but as surfaced passages from the letter that arrive at the right moments, like notes left on a trail by someone who walked it before you.',
      implementation: 'The Founder\'s Thread system surfaces letter passages at key journey moments. The companion voice layer carries the founder\'s register throughout the garden.',
      color: '#c9a84c',
      status: 'implemented',
    },
    {
      number: '02',
      title: 'The Fitra Verse (30:30) Must Be Restored',
      arabic: 'الْفِطْرَة',
      description: 'It\'s the theological foundation of the entire platform — the claim that the capacity for recognition is already in us before the platform teaches anything. It should appear on the Open Letter page, on the Keys page, and as the garden\'s bedrock layer.',
      implementation: 'Quran 30:30 appears in the Caretaker section. The Fitra bedrock layer is present in My Garden\'s Framework Map. The Keys page references it under Key 01.',
      color: '#2d7a47',
      status: 'implemented',
    },
    {
      number: '03',
      title: 'The Vernacular Register Must Be Honored',
      arabic: 'اللُّغَة الشَّعْبِيَّة',
      description: '"Life starts at the checkout, we still in the aisles" is not decoration. It\'s the cultural voice of the project declaring that this framework belongs to a specific community. The garden\'s language should be register-fluid: formal for architecture, vernacular for direct address.',
      implementation: 'The dual voice system carries both registers. Vernacular loading messages and greetings from the letter\'s register appear throughout the garden.',
      color: '#8a8ab8',
      status: 'implemented',
    },
    {
      number: '04',
      title: 'Garment and Garden Must Be Unified',
      arabic: 'وَحْدَة الثَّوْب وَالْجَنَّة',
      description: '"These bodies are the garments we wear, the seeds we plant" — one sentence, one concept. The Reshatal Taqwah and the Garden are two views of the same journey. They should be rendered as one system viewed from two angles.',
      implementation: 'The Garment-Garden Feedback Loop in My Garden shows how cultivation generates garment threads and garment state influences module responsiveness.',
      color: '#8a6a2a',
      status: 'implemented',
    },
    {
      number: '05',
      title: 'The Fikr Journal Must Exist as a Freeform Mode',
      arabic: 'مَجَلَّة الْفِكْر',
      description: 'The letter itself is "sample examinations, commentary, and field notes." The garden should support all three modes of documentation — structured examination, reflective commentary, and raw field notes — with equal respect. Not every encounter needs to be fissioned. Sometimes you just need to write down that you walked on water.',
      implementation: 'The Fikr Journal freeform mode is available in My Garden. No prompts, no frameworks — just writing. Field notes from the journey.',
      color: '#2d8a87',
      status: 'implemented',
    },
  ];

  const handleSaveFikr = () => {
    if (fikrText.trim().length < 5) return;
    if (typeof window !== 'undefined') {
      const existing = JSON.parse(localStorage.getItem('haal_fikr_entries') || '[]');
      existing.unshift({ text: fikrText, timestamp: new Date().toISOString(), source: 'open-letter' });
      localStorage.setItem('haal_fikr_entries', JSON.stringify(existing.slice(0, 50)));
    }
    setFikrSaved(true);
    setTimeout(() => { setFikrSaved(false); setFikrText(''); }, 2000);
  };

  return (
    <div style={{
      background: 'rgba(6,13,18,0.8)',
      border: '1px solid rgba(201,168,76,0.2)',
      borderRadius: 10,
      overflow: 'hidden',
      margin: '24px 0',
    }}>
      {/* Header */}
      <div style={{
        padding: '18px 20px 14px',
        borderBottom: '1px solid rgba(201,168,76,0.1)',
        background: 'rgba(201,168,76,0.03)',
      }}>
        <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 6 }}>
          ◈ SECTION 2 — FIVE ARCHITECTURAL DEMANDS
        </div>
        <div style={{ fontSize: 15, color: '#d4c5a9', marginBottom: 6 }}>
          What Section 2 Demands of the Platform
        </div>
        <p style={{ fontSize: 12, color: '#7a6f5d', lineHeight: 1.7 }}>
          Section 2 makes five demands of the platform that are currently being addressed. 
          Each demand is an architectural reinforcement — not a feature, but a foundation.
        </p>
      </div>

      {/* Demands */}
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {demands.map((d, i) => (
            <div key={d.number} style={{
              background: activeDemand === i ? `${d.color}08` : 'rgba(6,13,18,0.4)',
              border: `1px solid ${activeDemand === i ? d.color + '35' : 'rgba(201,168,76,0.1)'}`,
              borderRadius: 6,
              overflow: 'hidden',
              transition: 'all 0.2s',
            }}>
              <button
                onClick={() => setActiveDemand(activeDemand === i ? null : i)}
                style={{
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '12px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  textAlign: 'left',
                }}
              >
                <span style={{
                  fontSize: 9,
                  color: d.color,
                  background: `${d.color}15`,
                  border: `1px solid ${d.color}30`,
                  borderRadius: 3,
                  padding: '3px 7px',
                  flexShrink: 0,
                  fontFamily: 'monospace',
                }}>
                  {d.number}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: '#d4c5a9', marginBottom: 2 }}>{d.title}</div>
                  <div style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: `${d.color}70` }}>{d.arabic}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 8, color: '#2d7a47', background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 3, padding: '2px 5px' }}>
                    ✓ active
                  </span>
                  <span style={{ color: d.color, fontSize: 12 }}>{activeDemand === i ? '−' : '+'}</span>
                </div>
              </button>

              {activeDemand === i && (
                <div style={{ padding: '0 14px 14px', animation: 'demandExpand 0.2s ease' }}>
                  <style>{`@keyframes demandExpand { from { opacity: 0; } to { opacity: 1; } }`}</style>
                  <p style={{ fontSize: 12, color: '#d4c5a9', lineHeight: 1.75, marginBottom: 10 }}>
                    {d.description}
                  </p>
                  <div style={{
                    background: `${d.color}06`,
                    border: `1px solid ${d.color}15`,
                    borderRadius: 5,
                    padding: '10px 12px',
                  }}>
                    <div style={{ fontSize: 8, color: d.color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                      IMPLEMENTATION
                    </div>
                    <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.6 }}>{d.implementation}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Fikr Journal — Demand 05 in action */}
        <div style={{
          background: 'rgba(45,138,135,0.05)',
          border: '1px solid rgba(45,138,135,0.2)',
          borderRadius: 8,
          padding: '16px',
        }}>
          <div style={{ fontSize: 8, color: '#2d8a87', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>
            ◎ FIKR JOURNAL — DEMAND 05 IN ACTION
          </div>
          <p style={{ fontSize: 11, color: '#9a8f7d', lineHeight: 1.6, marginBottom: 10, fontStyle: 'italic' }}>
            "I noticed the sand doing it too." — Write what you see. No prompts. No frameworks. 
            Sometimes you just need to write down that you walked on water.
          </p>
          <textarea
            value={fikrText}
            onChange={e => setFikrText(e.target.value)}
            placeholder="Write freely. Field notes from the journey..."
            style={{
              width: '100%',
              minHeight: 80,
              background: 'rgba(6,13,18,0.5)',
              border: '1px solid rgba(45,138,135,0.15)',
              borderRadius: 5,
              color: '#d4c5a9',
              fontSize: 13,
              lineHeight: 1.7,
              padding: '10px 12px',
              resize: 'vertical',
              fontFamily: "'Georgia', serif",
              outline: 'none',
              boxSizing: 'border-box',
              marginBottom: 8,
            }}
          />
          <button
            onClick={handleSaveFikr}
            disabled={fikrText.trim().length < 5}
            style={{
              background: fikrSaved ? 'rgba(45,122,71,0.15)' : 'rgba(45,138,135,0.1)',
              border: `1px solid ${fikrSaved ? 'rgba(45,122,71,0.4)' : 'rgba(45,138,135,0.3)'}`,
              color: fikrSaved ? '#2d7a47' : '#2d8a87',
              fontSize: 10,
              padding: '7px 14px',
              cursor: fikrText.trim().length < 5 ? 'not-allowed' : 'pointer',
              borderRadius: 4,
              letterSpacing: '.06em',
              opacity: fikrText.trim().length < 5 ? 0.5 : 1,
              transition: 'all 0.2s',
            }}
          >
            {fikrSaved ? '✓ Saved to Fikr Journal' : '→ Save to Fikr Journal'}
          </button>
        </div>
      </div>
    </div>
  );
}
